import * as Sentry from "@sentry/nextjs";
import {compiler} from "markdown-to-jsx";
import type {MarkdownToJSX} from "markdown-to-jsx";
import Link from "next/link";
import React, {useEffect, useState} from "react";
import {getTransString} from "src/components/_common/Carbon";

import {parseHrefForLink} from "./urls";

type Props = {
  href: string;
  title?: string;
  className?: string;
  children: string;
};

const MarkdownLink: React.FC<Props> = ({href, children, title, className}) => {
  const [parsed, setParsed] = useState<ReturnType<typeof parseHrefForLink>>(href);
  useEffect(() => {
    try {
      setParsed(parseHrefForLink(href));
    } catch {
      setParsed({
        pathname: "/",
        query: {},
      });
      Sentry.captureException("Failed to parse href", scope => {
        scope.setContext("Context", {href, children: getTransString(children)});
        return scope;
      });
    }
  }, []);

  return typeof parsed !== "string" ? (
    <Link href={parsed} title={title} className={className}>
      {children}
    </Link>
  ) : (
    <span>
      <a
        href={parsed}
        title={title}
        className={className}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
        <span className="visually-hidden">(opens in new window)</span>
      </a>
      <span className="ml1 cIcon-linkout" aria-hidden="true" />
    </span>
  );
};

export const markdown = (
  content: string,
  linkColorBlue?: boolean,
  options?: MarkdownToJSX.Options,
): ReturnType<typeof compiler> =>
  compiler(content, {
    overrides: {
      a: {
        component: MarkdownLink,
        props: {
          className: `tdu${linkColorBlue ? " blue" : ""}`,
        },
      },
    },
    ...options,
  });
